import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Table,
  Form,
  InputNumber,
  Space,
  App,
  DatePicker,
  Spin,
} from 'antd';
import { EditOutlined, CloseOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { ApiPropertyAreaSimple, ApiTargetKawasan } from '../../types/rencana';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

// --- Editable Cell Component ---
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'number';
  record: any;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = <InputNumber style={{ width: '100%' }} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value!.replace(/\$\s?|(,*)/g, '')} />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={[record.key, dataIndex]}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Mohon isi ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --- Main Page Component ---
const TargetKawasanPage: React.FC = () => {
  const [form] = Form.useForm();
  const [allTargets, setAllTargets] = useState<ApiTargetKawasan[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<ApiPropertyAreaSimple[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
  const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(dayjs());
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canEdit = hasPermission('targetKawasan.update');

  const fetchData = useCallback(async (year: number) => {
    setLoading(true);
    try {
      const [targetsRes, areasRes] = await Promise.all([
        api.get('/targets/area', { params: { periode: year } }),
        api.get('/property-areas/role'),
      ]);
      setAllTargets(targetsRes.data.data || []);
      setPropertyAreas(areasRes.data.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error("Gagal memuat data target atau area properti.");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    if (selectedYear) {
      fetchData(selectedYear.year());
    }
  }, [selectedYear, fetchData]);

  const isEditing = (record: any) => editingKeys.has(record.key);

  const toggleEdit = (key: string) => {
    const newEditingKeys = new Set(editingKeys);
    if (newEditingKeys.has(key)) {
      newEditingKeys.delete(key);
    } else {
      newEditingKeys.add(key);
      const record = displayData.find(item => item.key === key);
      form.setFieldsValue({ [key]: record });
    }
    setEditingKeys(newEditingKeys);
  };

  const displayData = useMemo(() => {
    if (!selectedYear) return [];

    return propertyAreas.map(area => {
      const existingTarget = allTargets.find(t => t.property_area_id === area.id);
      const unitTarget = existingTarget?.unit_target || 0;
      const conversionRatio = existingTarget?.conversion_ratio || 0;

      const leadsDibutuhkan = (unitTarget && conversionRatio)
        ? Math.ceil(unitTarget / (conversionRatio / 100))
        : 0;

      return {
        key: area.id,
        target_id: existingTarget?.id || null,
        property_area_id: area.id,
        property_area_name: area.name,
        unit_target: unitTarget,
        omzet_target: existingTarget?.omzet_target || 0,
        conversion_ratio: conversionRatio,
        leadsDibutuhkan: leadsDibutuhkan,
      };
    });
  }, [allTargets, propertyAreas, selectedYear]);

  const formValues = Form.useWatch([], form);

  useEffect(() => {
    const formValuesToSet = displayData.reduce((acc, item) => {
      acc[item.key] = item;
      return acc;
    }, {} as any);
    form.setFieldsValue(formValuesToSet);
  }, [displayData, form]);

  const totalLeadsDibutuhkan = useMemo(() => {
    return displayData.reduce((sum, item) => {
      const currentValues = formValues?.[item.key] || item;
      const unitTarget = currentValues.unit_target || 0;
      const conversionRatio = currentValues.conversion_ratio || 0;
      const leadsNeeded = conversionRatio > 0 ? Math.ceil(unitTarget / (conversionRatio / 100)) : 0;
      return sum + leadsNeeded;
    }, 0);
  }, [displayData, formValues]);


  const columns = [
    {
      title: 'Kawasan',
      dataIndex: 'property_area_name',
      key: 'property_area_name',
      width: '25%',
    },
    {
      title: 'Rencana OK',
      children: [
        {
          title: 'Unit',
          dataIndex: 'unit_target',
          key: 'unit_target',
          editable: true,
          render: (val: number) => val.toLocaleString(),
        },
        {
          title: 'Omzet (Rp)',
          dataIndex: 'omzet_target',
          key: 'omzet_target',
          editable: true,
          render: (val: number) => val.toLocaleString(),
        },
      ],
    },
    {
      title: 'Rasio Konversi (%)',
      dataIndex: 'conversion_ratio',
      key: 'conversion_ratio',
      editable: true,
      render: (val: number) => `${val}%`,
    },
    {
      title: 'Jumlah Leads Dibutuhkan',
      dataIndex: 'leadsDibutuhkan',
      key: 'leadsDibutuhkan',
      render: (_: any, record: any) => {
        const formValues = form.getFieldsValue();
        const currentValues = formValues[record.key] || record;
        const unitTarget = currentValues.unit_target || 0;
        const conversionRatio = currentValues.conversion_ratio || 0;
        const leadsNeeded = conversionRatio > 0 ? Math.ceil(unitTarget / (conversionRatio / 100)) : 0;
        return leadsNeeded.toLocaleString();
      }
    },
    {
      title: 'Aksi',
      dataIndex: 'action',
      render: (_: any, record: any) => {
        if (!canEdit) return null;
        const editable = isEditing(record);
        return editable ? (
            <Button onClick={() => toggleEdit(record.key)} icon={<CloseOutlined />}>Batal</Button>
        ) : (
          <Button
            onClick={() => toggleEdit(record.key)}
            icon={<EditOutlined />}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col: any) => {
    if (col.children) {
      return {
        ...col,
        children: col.children.map((childCol: any) => {
          if (!childCol.editable) return childCol;
          return {
            ...childCol,
            onCell: (record: any) => ({
              record,
              inputType: 'number',
              dataIndex: childCol.dataIndex,
              title: childCol.title,
              editing: isEditing(record),
            }),
          };
        }),
      };
    }
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        inputType: 'number',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const handleSubmitAll = async () => {
    if (editingKeys.size === 0) {
      message.info('Tidak ada data yang sedang diubah untuk disimpan. Klik "Edit" pada salah satu baris terlebih dahulu.');
      return;
    }
    setSaving(true);
    try {
      const formValues = await form.validateFields();
      const currentYear = selectedYear!.year();

      const payload = Array.from(editingKeys).map(key => {
        const item = displayData.find(d => d.key === key)!;
        const editedValues = formValues[key] || {};
        return {
          target_id: item.target_id,
          property_area_id: item.property_area_id,
          periode: currentYear,
          unit_target: editedValues.unit_target ?? item.unit_target,
          omzet_target: editedValues.omzet_target ?? item.omzet_target,
          conversion_ratio: editedValues.conversion_ratio ?? item.conversion_ratio,
        };
      });

      await api.post('/targets/area', payload);

      setEditingKeys(new Set());
      message.success(`Berhasil menyimpan target untuk tahun ${currentYear}.`);
      fetchData(currentYear); // Refresh data
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
      message.error('Gagal menyimpan, mohon periksa kembali semua isian Anda.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space direction="vertical" size={0}>
            <Space align="center">
              <Title level={2} style={{ margin: 0 }}>
                Target Kawasan
              </Title>
              <DatePicker
                picker="year"
                value={selectedYear}
                onChange={(date) => setSelectedYear(date)}
                style={{ marginLeft: 16 }}
                allowClear={false}
              />
            </Space>
            <Paragraph type="secondary" style={{ margin: 0, marginTop: 4 }}>
              Total rencana leads yang dibutuhkan dari semua kawasan sebanyak{' '}
              <strong>{totalLeadsDibutuhkan.toLocaleString()}</strong>
            </Paragraph>
          </Space>
        </Col>
      </Row>

      <Card>
        <Spin spinning={loading}>
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              bordered
              dataSource={displayData}
              columns={mergedColumns.filter(c => c.dataIndex !== 'action' || canEdit)}
              rowClassName="editable-row"
              pagination={false}
              locale={{ emptyText: 'Tidak ada data area properti atau target untuk tahun yang dipilih.' }}
            />
          </Form>
          {canEdit && (
            <Row justify="end" style={{ marginTop: 24 }}>
              <Col>
                <Button
                  type="primary"
                  onClick={handleSubmitAll}
                  loading={saving}
                  icon={<SaveOutlined />}
                  disabled={editingKeys.size === 0}
                >
                  Kunci & Simpan Semua Target
                </Button>
              </Col>
            </Row>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default TargetKawasanPage;
